// Ленивые экраны и строки их областей (HQ-667). `lazy` — React.lazy, который вместе с куском экрана грузит куски строк
// областей, привязанных к экрану через bindStrings (extra/<область>.json): параллельно, экран рисуется, когда готовы
// оба, — ключи вместо текста не мелькают. Экран без привязки (матч, меню, «Скоро» — их строки в основном куске)
// грузится как обычный React.lazy. Привязки — таблицей в App.tsx (сверяет tests/shell/lazyStrings.test.tsx).
// Кусок экрана или строк не загрузился (после выката старых кусков нет) — одна перезагрузка на версию сборки (HQ-883,
// ui/chunkReload.ts); повтор — экран ошибки маршрута.
import { type ComponentType, lazy as reactLazy, type LazyExoticComponent } from 'react';
import type { StringArea } from './extraFiles';
import { loadArea } from './i18n';
import { withChunkReload } from '../ui/chunkReload';

const areasOf = new WeakMap<object, readonly StringArea[]>();

export function lazy<T extends ComponentType<any>>(load: () => Promise<{ default: T }>): LazyExoticComponent<T> {
  const screen: LazyExoticComponent<T> = reactLazy(withChunkReload(() =>
    Promise.all([load(), ...(areasOf.get(screen) ?? []).map(loadArea)]).then(([module]) => module)));
  return screen;
}

/** Привязать к ленивым экранам области строк. Вызывается до первого рендера экрана (при загрузке модуля маршрутов). */
export function bindStrings(pairs: ReadonlyArray<readonly [screen: ComponentType, areas: StringArea | readonly StringArea[]]>): void {
  for (const [screen, areas] of pairs) areasOf.set(screen, typeof areas === 'string' ? [areas] : areas);
}

/** Области строк, привязанные к экрану. */
export const stringAreas = (screen: ComponentType): readonly StringArea[] => areasOf.get(screen) ?? [];
